import { useState } from 'react'
import type { ComponentType } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDown, FileCog, GitMerge, Mountain, Scissors, Split } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ApplyTerrainElevationDialog } from '@/components/edit/apply-terrain-elevation-dialog'
import { CropDialog } from '@/components/edit/crop-dialog'
import { InterpolateGpsDialog } from '@/components/edit/interpolate-gps-dialog'
import { MergeDialog } from '@/components/edit/merge-dialog'
import { MetadataDialog } from '@/components/edit/metadata-dialog'
import { RemoveGpsOnlyDialog } from '@/components/edit/remove-gps-only-dialog'
import { RemoveIntervalDialog } from '@/components/edit/remove-interval-dialog'
import { SetGpsConstantDialog } from '@/components/edit/set-gps-constant-dialog'
import { ShiftTimeDialog } from '@/components/edit/shift-time-dialog'
import { SplitDialog } from '@/components/edit/split-dialog'
import { TerrainLoadDialog } from '@/components/terrain/terrain-load-dialog'
import { cn } from '@/lib/utils'
import {
  selectSelectedActivity,
  selectTerrainState,
  useActivityStore,
} from '@/store/activity-store'

type DialogKind =
  | 'crop'
  | 'removeInterval'
  | 'removeGpsOnly'
  | 'setGpsConstant'
  | 'interpolateGps'
  | 'shiftTime'
  | 'editMetadata'
  | 'merge'
  | 'split'
  | 'loadTerrain'
  | 'applyTerrain'

/**
 * The toolbar's "Edit" menu -- only rendered when an activity is
 * selected (per the "no placeholder UI" rule: there is nothing to edit
 * otherwise, so the control doesn't exist rather than existing disabled).
 * Each entry is a genuinely distinct operation per the master spec's
 * "never conflate remove/removeGpsOnly/setGpsConstant/interpolateGps/
 * repairOutlier behind one control" requirement -- `repairOutlier` has no
 * entry here since it's reached from the map's GPS-jump popup instead.
 *
 * M7 adds `editMetadata` (scoped to exactly what `applyOperation`
 * actually patches -- see `MetadataDialog`'s doc comment for why that's
 * narrower than "metadata" might suggest) and `merge` (picks a second
 * already-loaded activity from the sidebar's list -- see `MergeDialog`).
 *
 * Every dialog component here is always mounted (never conditionally
 * rendered by `dialog === 'x'`) so each one's own local state (the
 * in-progress range, picked coordinates, etc.) survives being closed and
 * reopened -- several of them (`setGpsConstant`'s map-picking,
 * `split`'s non-modal timeline-picking) depend on exactly that.
 */
export function EditMenu() {
  const hasSelection = useActivityStore((s) => selectSelectedActivity(s) != null)
  const terrain = useActivityStore((s) => selectTerrainState(s, s.selectedActivityId ?? undefined))
  const [dialog, setDialog] = useState<DialogKind | null>(null)

  if (!hasSelection) return null

  const terrainLoading = terrain.status === 'loading'
  const terrainLoaded = terrain.source != null && terrain.status !== 'error'

  const item = (
    kind: DialogKind,
    label: string,
    Icon: ComponentType<{ className?: string }>,
    disabledReason?: string,
  ) => (
    <DropdownMenu.Item
      className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:text-muted-foreground flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled]:pointer-events-none"
      disabled={disabledReason != null}
      onSelect={() => setDialog(kind)}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="flex flex-col">
        {label}
        {disabledReason != null && <span className="text-xs">{disabledReason}</span>}
      </span>
    </DropdownMenu.Item>
  )

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button variant="outline" size="sm">
            Edit
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={4}
            className={cn(
              'border-border bg-popover text-popover-foreground z-50 min-w-56 rounded-md border p-1 shadow-md',
            )}
          >
            {item('crop', 'Crop…', Scissors)}
            {item('removeInterval', 'Remove interval…', Scissors)}
            {item('removeGpsOnly', 'Remove GPS only…', Scissors)}
            {item('setGpsConstant', 'Set GPS constant…', Scissors)}
            {item('interpolateGps', 'Interpolate GPS…', Scissors)}
            {item('shiftTime', 'Shift time…', Scissors)}
            {item('editMetadata', 'Edit metadata…', FileCog)}
            {item('merge', 'Merge…', GitMerge)}
            <DropdownMenu.Separator className="bg-border my-1 h-px" />
            {item('split', 'Split…', Split)}
            <DropdownMenu.Separator className="bg-border my-1 h-px" />
            {item(
              'loadTerrain',
              'Load terrain elevation…',
              Mountain,
              terrainLoading ? 'Loading…' : undefined,
            )}
            {item(
              'applyTerrain',
              'Apply terrain elevation to file…',
              Mountain,
              terrainLoaded ? undefined : 'Load terrain elevation first',
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <CropDialog
        open={dialog === 'crop'}
        onOpenChange={(open) => setDialog(open ? 'crop' : null)}
      />
      <RemoveIntervalDialog
        open={dialog === 'removeInterval'}
        onOpenChange={(open) => setDialog(open ? 'removeInterval' : null)}
      />
      <RemoveGpsOnlyDialog
        open={dialog === 'removeGpsOnly'}
        onOpenChange={(open) => setDialog(open ? 'removeGpsOnly' : null)}
      />
      <SetGpsConstantDialog
        open={dialog === 'setGpsConstant'}
        onOpenChange={(open) => setDialog(open ? 'setGpsConstant' : null)}
      />
      <InterpolateGpsDialog
        open={dialog === 'interpolateGps'}
        onOpenChange={(open) => setDialog(open ? 'interpolateGps' : null)}
      />
      <ShiftTimeDialog
        open={dialog === 'shiftTime'}
        onOpenChange={(open) => setDialog(open ? 'shiftTime' : null)}
      />
      <MetadataDialog
        open={dialog === 'editMetadata'}
        onOpenChange={(open) => setDialog(open ? 'editMetadata' : null)}
      />
      <MergeDialog
        open={dialog === 'merge'}
        onOpenChange={(open) => setDialog(open ? 'merge' : null)}
      />
      <SplitDialog
        open={dialog === 'split'}
        onOpenChange={(open) => setDialog(open ? 'split' : null)}
      />
      <TerrainLoadDialog
        open={dialog === 'loadTerrain'}
        onOpenChange={(open) => setDialog(open ? 'loadTerrain' : null)}
      />
      <ApplyTerrainElevationDialog
        open={dialog === 'applyTerrain'}
        onOpenChange={(open) => setDialog(open ? 'applyTerrain' : null)}
      />
    </>
  )
}
